<template>
  <v-app class="admin-layout-app">
    <v-layout class="admin-layout">
      <!-- ====================================================== -->
      <!-- UTILITY / COMMERCE SYSTEM BAR -->
      <!-- ====================================================== -->

      <v-system-bar
        flat
        elevation="0"
        height="28"
        class="admin-utility-bar"
      >
        <div class="utility-bar-inner">
          <!-- ================================================== -->
          <!-- LEFT SIDE -->
          <!-- ================================================== -->

          <div class="utility-left">
            <!-- CONTACT -->
            <button
              type="button"
              class="utility-link"
              @click="goToContact"
            >
              {{
                lang === 'fr'
                  ? 'Contactez-nous'
                  : 'Contact us'
              }}
            </button>

            <!-- ACCOUNT -->
            <v-menu
              v-model="accountMenu"
              location="bottom start"
              offset="4"
            >
              <template #activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="utility-account"
                >
                  <v-icon size="14">
                    mdi-account
                  </v-icon>

                  <span class="account-name">
                    {{ displayName }}
                  </span>

                  <v-icon size="14">
                    mdi-chevron-down
                  </v-icon>
                </button>
              </template>

              <v-card
                min-width="220"
                elevation="8"
              >
                <v-list density="compact">
                  <v-list-item
                    prepend-icon="mdi-account-outline"
                    :title="
                      lang === 'fr'
                        ? 'Mon profil'
                        : 'My Profile'
                    "
                    @click="goToProfile"
                  />

                  <v-list-item
                    prepend-icon="mdi-history"
                    :title="
                      lang === 'fr'
                        ? 'Activité'
                        : 'Activity'
                    "
                    @click="goToActivity"
                  />

                  <v-divider />

                  <v-list-item
                    prepend-icon="mdi-logout-variant"
                    :title="
                      lang === 'fr'
                        ? 'Déconnexion'
                        : 'Logout'
                    "
                    @click="handleLogout"
                  />
                </v-list>
              </v-card>
            </v-menu>
          </div>

          <!-- ================================================== -->
          <!-- RIGHT SIDE -->
          <!-- ================================================== -->

          <div class="utility-right">
            <!-- SEARCH -->
            <v-tooltip
              location="bottom"
              :text="
                lang === 'fr'
                  ? 'Rechercher'
                  : 'Search'
              "
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="x-small"
                  variant="text"
                  class="utility-icon-button"
                  :aria-label="
                    lang === 'fr'
                      ? 'Rechercher'
                      : 'Search'
                  "
                  @click="searchDialogOpen = true"
                >
                  <v-icon size="17">
                    mdi-magnify
                  </v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <!-- CART -->
            <v-tooltip
              location="bottom"
              :text="
                lang === 'fr'
                  ? 'Panier'
                  : 'Cart'
              "
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="x-small"
                  variant="text"
                  class="utility-icon-button"
                  :aria-label="
                    lang === 'fr'
                      ? 'Panier'
                      : 'Cart'
                  "
                  @click="openCart"
                >
                  <v-badge
                    :content="itemCount"
                    color="error"
                    :model-value="itemCount > 0"
                  >
                    <v-icon size="17">
                      mdi-cart-outline
                    </v-icon>
                  </v-badge>
                </v-btn>
              </template>
            </v-tooltip>

            <!-- CURRENCY -->
            <v-menu
              v-model="currencyMenu"
              location="bottom end"
              offset="4"
            >
              <template #activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="utility-currency"
                >
                  {{ selectedCurrency.label }}

                  <v-icon size="13">
                    mdi-chevron-down
                  </v-icon>
                </button>
              </template>

              <v-list density="compact">
                <v-list-item
                  v-for="currency in currencies"
                  :key="currency.code"
                  :title="currency.label"
                  :active="
                    selectedCurrency.code ===
                    currency.code
                  "
                  @click="
                    selectCurrency(
                      currency
                    )
                  "
                />
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-system-bar>

      <!-- ====================================================== -->
      <!-- SEARCH DIALOG -->
      <!-- ====================================================== -->

      <SearchDialogheader
        v-model="searchDialogOpen"
      />

      <!-- ====================================================== -->
      <!-- TOP ADMIN APP BAR -->
      <!-- ====================================================== -->

      <v-app-bar
        flat
        elevation="0"
        height="64"
        class="admin-app-bar"
        :style="{
          background:
            themeStore.themeStyles.background,
          color:
            themeStore.themeStyles.color
        }"
      >
        <!-- ==================================================== -->
        <!-- SIDEBAR CONTROLS -->
        <!-- ==================================================== -->

        <div class="admin-sidebar-controls">
          <v-btn
            icon
            size="small"
            variant="text"
            @click="adminUi.toggleSidebar"
          >
            <v-icon>
              mdi-menu
            </v-icon>
          </v-btn>

          <v-btn
            icon
            size="small"
            variant="text"
            @click="adminUi.togglePin"
          >
            <v-icon
              :color="
                adminUi.isPinned
                  ? 'primary'
                  : ''
              "
            >
              mdi-pin
            </v-icon>
          </v-btn>
        </div>

        <!-- ==================================================== -->
        <!-- LOGO -->
        <!-- ==================================================== -->

        <router-link
          :to="{ name: 'PublicHome' }"
          class="admin-logo-link"
        >
          <v-img
            v-if="
              getCurrentTheme ===
              'light'
            "
            :src="getLogoLight"
            class="admin-logo"
            contain
          />

          <v-img
            v-else
            :src="getLogoDark"
            class="admin-logo"
            contain
          />
        </router-link>

        <v-divider
          vertical
          class="admin-app-divider mx-1"
        />

        <!-- ==================================================== -->
        <!-- DESKTOP MENUS -->
        <!-- ==================================================== -->

        <div
          v-if="mdAndUp"
          class="admin-top-menus"
        >
          <MegaMenuSetup />

          <MegaMenu />

          <MegaMenuResources />
        </div>

        <!-- ==================================================== -->
        <!-- MOBILE TOP MENU BUTTON -->
        <!-- ==================================================== -->

        <v-btn
          v-else
          icon
          size="small"
          variant="text"
          class="admin-mobile-menu-button"
          @click="
            mobileTopMenuOpen =
              !mobileTopMenuOpen
          "
        >
          <v-icon size="24">
            {{
              mobileTopMenuOpen
                ? 'mdi-close'
                : 'mdi-menu'
            }}
          </v-icon>
        </v-btn>

        <v-spacer />

        <v-divider
          vertical
          class="admin-app-divider mx-3"
        />

        <!-- ==================================================== -->
        <!-- SETTINGS -->
        <!-- ==================================================== -->

        <v-menu
          v-model="settingsMenu"
          :close-on-content-click="false"
          location="bottom end"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              v-ripple
              class="
                icon-btn
                d-inline-flex
                align-center
                justify-center
              "
            >
              <v-icon size="large">
                mdi-theme-light-dark
              </v-icon>
            </span>
          </template>

          <v-card min-width="300">
            <v-list>
              <v-list-item>
                <template #prepend>
                  <v-avatar
                    color="primary"
                    size="38"
                  >
                    <v-icon>
                      mdi-account
                    </v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>
                  {{ displayName }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  {{
                    lang === 'fr'
                      ? 'Compte utilisateur'
                      : 'User account'
                  }}
                </v-list-item-subtitle>

                <template #append>
                  <v-btn
                    icon="mdi-logout-variant"
                    variant="text"
                    @click="handleLogout"
                  />
                </template>
              </v-list-item>
            </v-list>

            <v-divider />

            <!-- THEME SWITCHES -->
            <v-row class="ma-2">
              <v-col
                cols="4"
                class="text-center"
              >
                <v-switch
                  v-model="lightSwitch"
                  color="purple"
                  hide-details
                  @change="
                    selectTheme('light')
                  "
                />

                <div>
                  Light
                </div>
              </v-col>

              <v-col
                cols="4"
                class="text-center"
              >
                <v-switch
                  v-model="darkSwitch"
                  color="purple"
                  hide-details
                  @change="
                    selectTheme('dark')
                  "
                />

                <div>
                  Dark
                </div>
              </v-col>

              <v-col
                cols="4"
                class="text-center"
              >
                <v-switch
                  v-model="hybridSwitch"
                  color="purple"
                  hide-details
                  @change="
                    selectTheme('hybrid')
                  "
                />

                <div>
                  Hybrid
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-menu>

        <!-- ==================================================== -->
        <!-- LANGUAGE -->
        <!-- ==================================================== -->

        <v-menu
          v-model="toprightmenu1"
          offset-y
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="formatlang"
            >
              <v-tooltip
                activator="parent"
                location="bottom"
              >
                {{
                  lang === 'fr'
                    ? 'Langue'
                    : 'Language'
                }}
              </v-tooltip>

              <LanguageSwitcher
                @change="
                  onLanguageChange
                "
              />
            </span>
          </template>
        </v-menu>
      </v-app-bar>

      <!-- ====================================================== -->
      <!-- MOBILE TOP MENU DRAWER -->
      <!-- ====================================================== -->

      <v-navigation-drawer
        v-model="mobileTopMenuOpen"
        temporary
        location="right"
        width="340"
        class="admin-mobile-top-menu"
      >
        <!-- HEADER -->
        <div class="mobile-menu-header">
          <div class="d-flex align-center">
            <v-icon
              size="22"
              class="mr-2"
            >
              mdi-apps
            </v-icon>

            <span class="mobile-menu-title">
              {{
                lang === 'fr'
                  ? 'Menu'
                  : 'Menu'
              }}
            </span>
          </div>

          <v-btn
            icon
            size="small"
            variant="text"
            @click="
              mobileTopMenuOpen =
                false
            "
          >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </div>

        <v-divider />

        <!-- ==================================================== -->
        <!-- MOBILE MENU ITEMS -->
        <!-- ==================================================== -->

        <v-list
          v-model:opened="mobileOpenedGroups"
          density="comfortable"
        >
          <!-- ================================================== -->
          <!-- SETUP -->
          <!-- ================================================== -->

          <v-list-group value="setup">
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-cog-outline"
                :title="
                  lang === 'fr'
                    ? 'Configurer'
                    : 'Setup'
                "
              />
            </template>

            <v-list-item
              prepend-icon="mdi-cogs"
              :title="
                lang === 'fr'
                  ? 'Opérations'
                  : 'Operations'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'OperationsFR'
                    : 'OperationsEN'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-folder-cog-outline"
              :title="
                lang === 'fr'
                  ? 'Projet'
                  : 'Project'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'ProjectCodeFR'
                    : 'ProjectCodeEN'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-handshake-outline"
              :title="
                lang === 'fr'
                  ? 'Partenaires d’affaires'
                  : 'Business Partners'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'BusinessPartnersFR'
                    : 'BusinessPartnersEN'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-target"
              :title="
                lang === 'fr'
                  ? 'Centre de coûts'
                  : 'Cost Center'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'CostCenterFR'
                    : 'CostCenterEN'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-office-building-outline"
              :title="
                lang === 'fr'
                  ? 'Département'
                  : 'Department'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'DepartmentsFR'
                    : 'DepartmentsEN'
                )
              "
            />
          </v-list-group>

          <!-- ================================================== -->
          <!-- ERP MODULES -->
          <!-- ================================================== -->

          <v-list-group value="erp">
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-apps"
                :title="
                  lang === 'fr'
                    ? 'Modules ERP'
                    : 'ERP Modules'
                "
              />
            </template>

            <v-list-item
              prepend-icon="mdi-shield-account-outline"
              title="Administration"
              @click="
                mobileNavigate(
                  'AdminDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-finance"
              title="Finance"
              @click="
                mobileNavigate(
                  'FinanceDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-warehouse"
              :title="
                lang === 'fr'
                  ? 'Inventaire et logistique'
                  : 'Inventory & Logistics'
              "
              @click="
                mobileNavigate(
                  'InventoryLogisticsDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-account-cash-outline"
              :title="
                lang === 'fr'
                  ? 'Paie'
                  : 'Payroll'
              "
              @click="
                mobileNavigate(
                  'PayrollDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-cart-arrow-down"
              :title="
                lang === 'fr'
                  ? 'Approvisionnement'
                  : 'Procurement'
              "
              @click="
                mobileNavigate(
                  'ProcurementDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-cart-outline"
              :title="
                lang === 'fr'
                  ? 'Ventes'
                  : 'Sales'
              "
              @click="
                mobileNavigate(
                  'SalesDashboard'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-hospital-box-outline"
              :title="
                lang === 'fr'
                  ? 'Médical / Clinique'
                  : 'Medical / Clinical'
              "
              @click="
                mobileNavigate(
                  'ClinicalDashboard'
                )
              "
            />
          </v-list-group>

          <!-- ================================================== -->
          <!-- RESOURCES -->
          <!-- ================================================== -->

          <v-list-group value="resources">
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-lifebuoy"
                :title="
                  lang === 'fr'
                    ? 'Ressources'
                    : 'Resources'
                "
              />
            </template>

            <v-list-item
              prepend-icon="mdi-book-open-page-variant-outline"
              :title="
                lang === 'fr'
                  ? 'Documentation'
                  : 'Documentation'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'OperationsFR'
                    : 'OperationsEN'
                )
              "
            />

            <v-list-item
              prepend-icon="mdi-headset"
              :title="
                lang === 'fr'
                  ? 'Support technique'
                  : 'Technical Support'
              "
              @click="
                mobileNavigate(
                  lang === 'fr'
                    ? 'ProjectCodeFR'
                    : 'ProjectCodeEN'
                )
              "
            />
          </v-list-group>
        </v-list>
      </v-navigation-drawer>

      <!-- ====================================================== -->
      <!-- ADMIN SIDEBAR -->
      <!-- ====================================================== -->

      <v-navigation-drawer
        v-model="adminUi.sidebarOpen"
        :permanent="
          adminUi.isPinned &&
          mdAndUp
        "
        :temporary="!mdAndUp"
        :width="drawerWidth"
        class="admin-navigation-drawer"
      >
        <AdminSidebar
          @resize="
            handleSidebarResize
          "
          @panel-state="
            handleSidebarResize
          "
        />
      </v-navigation-drawer>

      <!-- ====================================================== -->
      <!-- MAIN CONTENT -->
      <!-- ====================================================== -->

      <v-main
        class="admin-main-content"
      >
        <router-view />

        <AdminFooter />
      </v-main>
    </v-layout>
  </v-app>
</template>

<script setup>
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  watch
} from 'vue'

import {
  useRouter
} from 'vue-router'

import {
  useDisplay
} from 'vuetify'

import {
  useI18n
} from 'vue-i18n'

import AdminSidebar
  from '@/modules/admin/components/AdminSidebar.vue'

import AdminFooter
  from '@/modules/admin/components/AdminFooter.vue'

import MegaMenu
  from '@/components/MegaMenu.vue'

import MegaMenuSetup
  from '@/components/MegaMenuSetup.vue'

import MegaMenuResources
  from '@/components/MegaMenuResources.vue'

import LanguageSwitcher
  from '@/components/LanguageSwitcher.vue'

import SearchDialogheader
  from '@/components/SearchDialogheader.vue'

import api
  from '@/auth/api'

import {
  useThemeStore,
  useAuthStore
} from '@/store/auth'

import {
  useLangStore
} from '@/store/lang'

import {
  useAdminUiStore
} from '@/modules/admin/store/adminUi.store'

// ============================================================
// ROUTER / DISPLAY
// ============================================================

const router =
  useRouter()

const {
  mdAndUp
} = useDisplay()

// ============================================================
// STORES
// ============================================================

const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const langStore =
  useLangStore()

const adminUi =
  useAdminUiStore()

const {
  locale
} = useI18n()

// ============================================================
// LANGUAGE
// ============================================================

const lang =
  computed(
    () =>
      langStore.currentLanguage ||
      'en'
  )

watch(
  lang,
  newLanguage => {
    locale.value =
      newLanguage ||
      'en'
  },
  {
    immediate: true
  }
)

// ============================================================
// DISPLAY / RESPONSIVE
// ============================================================

const mobileTopMenuOpen =
  ref(false)

const mobileOpenedGroups =
  ref([])

watch(
  mdAndUp,
  desktop => {
    if (desktop) {
      mobileTopMenuOpen.value =
        false
    }
  }
)

// ============================================================
// USER
// ============================================================

const displayName =
  computed(
    () =>
      auth.loginName ||
      auth.user?.name ||
      auth.user?.displayName ||
      'Customer'
  )

// ============================================================
// CART STORE
// ============================================================

const cartStore =
  inject(
    'cartStore',
    null
  )

const itemCount =
  computed(() => {
    const value =
      cartStore?.itemCount

    if (
      value &&
      typeof value ===
        'object' &&
      'value' in value
    ) {
      return Number(
        value.value ||
        0
      )
    }

    return Number(
      value ||
      0
    )
  })

function openCart() {
  if (
    typeof cartStore
      ?.openCart ===
      'function'
  ) {
    cartStore.openCart()
    return
  }

  if (
    typeof cartStore
      ?.openDrawer ===
      'function'
  ) {
    cartStore.openDrawer()
    return
  }

  if (
    cartStore &&
    'drawerOpen' in
      cartStore
  ) {
    cartStore.drawerOpen =
      true

    return
  }

  console.log(
    '[AdminLayout] Cart clicked.'
  )
}

// ============================================================
// SEARCH DIALOG
// ============================================================

const searchDialogOpen =
  ref(false)

// ============================================================
// SIDEBAR WIDTH
// ============================================================

const ICON_RAIL_WIDTH =
  64

const DEFAULT_PANEL_WIDTH =
  280

const DEFAULT_DRAWER_WIDTH =
  ICON_RAIL_WIDTH +
  DEFAULT_PANEL_WIDTH

const drawerWidth =
  ref(
    DEFAULT_DRAWER_WIDTH
  )

function handleSidebarResize(
  detail
) {
  /*
   * Do not allow desktop resizing logic
   * to interfere with mobile drawer.
   */
  if (!mdAndUp.value) {
    return
  }

  const width =
    Number(
      detail?.totalWidth
    )

  if (
    !Number.isFinite(
      width
    ) ||
    width <
      ICON_RAIL_WIDTH
  ) {
    return
  }

  drawerWidth.value =
    width
}

// ============================================================
// MENUS
// ============================================================

const toprightmenu1 =
  ref(false)

const settingsMenu =
  ref(false)

const accountMenu =
  ref(false)

const currencyMenu =
  ref(false)

// ============================================================
// SETTINGS
// ============================================================

const lightSwitch =
  ref(false)

const darkSwitch =
  ref(false)

const hybridSwitch =
  ref(false)

// ============================================================
// CURRENCY
// ============================================================

const currencies = [
  {
    code:
      'CAD',

    label:
      'C$ CAD'
  },

  {
    code:
      'USD',

    label:
      '$ USD'
  },

  {
    code:
      'EUR',

    label:
      '€ EUR'
  }
]

const selectedCurrency =
  ref(
    currencies[0]
  )

function selectCurrency(
  currency
) {
  selectedCurrency.value =
    currency

  localStorage.setItem(
    'currency',
    currency.code
  )

  currencyMenu.value =
    false
}

// ============================================================
// TENANT / LOGOS
// ============================================================

const hospitalRef =
  ref(null)

const getLogoLight =
  ref('')

const getLogoDark =
  ref('')

const getCurrentTheme =
  ref('light')

// ============================================================
// LANGUAGE CHANGE
// ============================================================

function onLanguageChange(
  newLang
) {
  locale.value =
    newLang

  nextTick(() => {
    console.log(
      'Language updated, DOM updated safely'
    )
  })
}

// ============================================================
// THEME
// ============================================================

function selectTheme(
  selectedTheme
) {
  lightSwitch.value =
    selectedTheme ===
    'light'

  darkSwitch.value =
    selectedTheme ===
    'dark'

  hybridSwitch.value =
    selectedTheme ===
    'hybrid'

  themeStore.setTheme(
    selectedTheme
  )

  getCurrentTheme.value =
    selectedTheme

  localStorage.setItem(
    'currentTheme',
    selectedTheme
  )
}

// ============================================================
// MOBILE NAVIGATION
// ============================================================

async function mobileNavigate(
  routeName
) {
  if (!routeName) {
    return
  }

  /*
   * Close the mobile menu first so
   * navigation feels immediate.
   */
  mobileTopMenuOpen.value =
    false

  try {
    await router.push({
      name:
        routeName,

      params: {
        lang:
          lang.value
      }
    })
  }
  catch (error) {
    console.warn(
      `Unable to navigate to "${routeName}".`,
      error
    )
  }
}

// ============================================================
// UTILITY NAVIGATION
// ============================================================

function goToContact() {
  /*
   * Replace this when your contact
   * route is finalized.
   */

  console.log(
    '[AdminLayout] Contact clicked.'
  )
}

async function goToProfile() {
  const routeName =
    lang.value === 'en'
      ? 'EditProfileEN'
      : 'EditProfileFR'

  accountMenu.value =
    false

  try {
    await router.push({
      name:
        routeName
    })
  }
  catch (error) {
    console.warn(
      `Unable to navigate to "${routeName}".`,
      error
    )
  }
}

async function goToActivity() {
  accountMenu.value =
    false

  try {
    await router.push({
      name:
        'SaaSHostingActivity',

      params: {
        lang:
          lang.value
      }
    })
  }
  catch (error) {
    console.warn(
      'Unable to navigate to activity.',
      error
    )
  }
}

// ============================================================
// AUTH
// ============================================================

function handleLogout() {
  accountMenu.value =
    false

  mobileTopMenuOpen.value =
    false

  auth.logout()
}

// ============================================================
// INITIALIZE -->
// ============================================================

onMounted(
  async () => {
    // --------------------------------------------------------
    // CURRENT TENANT
    // --------------------------------------------------------

    try {
      const response =
        await api.get(
          '/Tenants/current'
        )

      hospitalRef.value =
        response.data

      if (
        hospitalRef.value
      ) {
        const subscriptionStatus =
          hospitalRef.value
            .subscriptionStatus

        if (
          subscriptionStatus ===
          0
        ) {
          await router.push({
            name:
              lang.value ===
              'en'
                ? 'BillingEN'
                : 'BillingFR'
          })
        }
      }
    }
    catch (error) {
      console.warn(
        'Unable to load current tenant.',
        error
      )
    }

    // --------------------------------------------------------
    // LOGOS
    // --------------------------------------------------------

    getLogoLight.value =
      localStorage.getItem(
        'LogoLight'
      ) ||
      ''

    getLogoDark.value =
      localStorage.getItem(
        'LogoDark'
      ) ||
      ''

    // --------------------------------------------------------
    // THEME
    // --------------------------------------------------------

    getCurrentTheme.value =
      localStorage.getItem(
        'currentTheme'
      ) ||
      'light'

    selectTheme(
      getCurrentTheme.value
    )

    // --------------------------------------------------------
    // CURRENCY
    // --------------------------------------------------------

    const savedCurrency =
      localStorage.getItem(
        'currency'
      )

    if (
      savedCurrency
    ) {
      const matchingCurrency =
        currencies.find(
          currency =>
            currency.code ===
            savedCurrency
        )

      if (
        matchingCurrency
      ) {
        selectedCurrency.value =
          matchingCurrency
      }
    }
  }
)
</script>

<style scoped>
/* ============================================================
   UTILITY BAR
============================================================ */

.admin-utility-bar {
  width:
    100% !important;

  height:
    28px !important;

  min-height:
    28px !important;

  padding:
    0 !important;

  background:
    #f0f1ff !important;

  color:
    #333333 !important;

  border-bottom:
    1px solid
    rgba(
      0,
      0,
      0,
      0.06
    );

  overflow:
    visible !important;

  z-index:
    1200;
}

.utility-bar-inner {
  display:
    flex;

  width:
    100%;

  height:
    28px;

  align-items:
    center;

  justify-content:
    space-between;

  padding:
    0
    30px;
}

.utility-left,
.utility-right {
  display:
    flex;

  height:
    100%;

  align-items:
    center;
}

.utility-right {
  margin-left:
    auto;

  gap:
    3px;
}

/* ============================================================
   UTILITY CONTROLS
============================================================ */

.utility-link,
.utility-account,
.utility-currency {
  border:
    0;

  background:
    transparent;

  color:
    inherit;

  cursor:
    pointer;

  font-family:
    inherit;

  font-size:
    11px;
}

.utility-link {
  padding:
    0
    6px;
}

.utility-account {
  display:
    flex;

  min-height:
    22px;

  align-items:
    center;

  gap:
    4px;

  margin-left:
    8px;

  padding:
    0
    7px;

  border-radius:
    4px;

  background:
    rgba(
      0,
      0,
      0,
      0.05
    );
}

.account-name {
  max-width:
    150px;

  overflow:
    hidden;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}

.utility-currency {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    2px;

  padding:
    0
    4px;
}

.utility-icon-button {
  width:
    26px !important;

  height:
    26px !important;
}

/* ============================================================
   APP BAR
============================================================ */

.admin-app-bar {
  width:
    100% !important;

  padding-left:
    30px !important;

  padding-right:
    30px !important;

  overflow:
    visible !important;

  z-index:
    1100;
}

/* ============================================================
   SIDEBAR CONTROLS
============================================================ */

.admin-sidebar-controls {
  display:
    flex;

  align-items:
    center;

  flex:
    0 0 auto;
}

/* ============================================================
   LOGO
============================================================ */

.admin-logo-link {
  display:
    inline-flex;

  align-items:
    center;

  flex:
    0 0 auto;

  text-decoration:
    none;
}

.admin-logo {
  width:
    200px;

  height:
    42px;
}

/* ============================================================
   DIVIDERS
============================================================ */

.admin-app-divider {
  align-self:
    stretch;

  min-height:
    28px;
}

/* ============================================================
   DESKTOP TOP MENUS
============================================================ */

.admin-top-menus {
  display:
    flex;

  min-width:
    0;

  align-items:
    center;
}

/*
 * The previous inline margin-top: -20px
 * is intentionally removed.
 *
 * Each menu should align naturally
 * with the app bar.
 */

/* ============================================================
   MOBILE MENU BUTTON
============================================================ */

.admin-mobile-menu-button {
  flex:
    0 0 auto;
}

/* ============================================================
   MOBILE TOP MENU DRAWER
============================================================ */

.admin-mobile-top-menu {
  z-index:
    2500 !important;
}

.mobile-menu-header {
  display:
    flex;

  min-height:
    58px;

  align-items:
    center;

  justify-content:
    space-between;

  padding:
    0
    15px;
}

.mobile-menu-title {
  font-size:
    16px;

  font-weight:
    700;
}

/* ============================================================
   SETTINGS
============================================================ */

.icon-btn {
  width:
    34px;

  height:
    34px;

  border-radius:
    50%;

  cursor:
    pointer;

  flex:
    0 0 auto;
}

/* ============================================================
   LANGUAGE
============================================================ */

.formatlang {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    2px;

  margin-left:
    10px;

  cursor:
    pointer;
}

/* ============================================================
   ADMIN SIDEBAR
============================================================ */

.admin-navigation-drawer {
  overflow:
    visible !important;
}

/* ============================================================
   ADMIN LAYOUT / SCROLL CONTAINMENT
   ------------------------------------------------------------
   IMPORTANT:
   These selectors are local to this AdminLayout component only.
   There are NO global html/body/#app/.v-application/.v-layout
   overflow rules here, so public pages keep normal scrolling.
============================================================ */

.admin-layout-app {
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
}

.admin-layout {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* ============================================================
   MAIN CONTENT
============================================================ */

.admin-main-content {
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/*
 * No margin-top or calc(100dvh - 92px) is necessary here.
 *
 * v-system-bar + v-app-bar are both registered
 * with the same Vuetify layout, so v-main receives the
 * correct layout offsets automatically.
 */

/* ============================================================
   DESKTOP
============================================================ */

@media (
  max-width: 1400px
) {
  .admin-logo {
    width:
      160px;
  }
}

/* ============================================================
   SMALL DESKTOP
============================================================ */

@media (
  max-width: 1200px
) {
  .utility-bar-inner {
    padding:
      0
      16px;
  }

  .admin-app-bar {
    padding-left:
      16px !important;

    padding-right:
      16px !important;
  }

  .admin-logo {
    width:
      150px;
  }
}

/* ============================================================
   TABLET
============================================================ */

@media (
  max-width: 959px
) {
  .utility-bar-inner {
    padding:
      0
      10px;
  }

  .admin-app-bar {
    padding-left:
      8px !important;

    padding-right:
      8px !important;
  }

  .admin-logo {
    width:
      135px;

    height:
      38px;
  }

  /*
   * The regular admin sidebar becomes
   * temporary on mobile/tablet.
   */
  .admin-navigation-drawer {
    z-index:
      2400 !important;
  }
}

/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 600px
) {
  .utility-bar-inner {
    padding:
      0
      6px;
  }

  .account-name {
    max-width:
      80px;
  }

  .admin-logo {
    width:
      105px;

    height:
      36px;
  }

  .admin-app-divider {
    display:
      none;
  }

  .utility-link,
  .utility-account,
  .utility-currency {
    font-size:
      10px;
  }

  .utility-account {
    margin-left:
      3px;

    padding:
      0
      4px;
  }

  .formatlang {
    margin-left:
      3px;
  }
}
</style>